/**
 * Shared tenant branding helpers.
 *
 * Applies tenant branding colors to CSS variables used by UI_SYSTEM.
 */

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const normalizeHex = (hex) => {
  if (!hex) return null;
  const cleaned = String(hex).trim().replace('#', '');
  if (cleaned.length === 3) {
    return `#${cleaned[0]}${cleaned[0]}${cleaned[1]}${cleaned[1]}${cleaned[2]}${cleaned[2]}`;
  }
  if (cleaned.length === 6) {
    return `#${cleaned}`;
  }
  return null;
};

const hexToHsl = (hex) => {
  const normalized = normalizeHex(hex);
  if (!normalized) return null;
  const value = normalized.replace('#', '');
  const r = parseInt(value.slice(0, 2), 16) / 255;
  const g = parseInt(value.slice(2, 4), 16) / 255;
  const b = parseInt(value.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
};

const toHslTriplet = ({ h, s, l }) => `${h} ${s}% ${l}%`;

export const applyTenantBranding = (branding) => {
  if (typeof document === 'undefined') return;
  const base = hexToHsl(branding?.primary_color);
  if (!base) return;

  const primary600 = { ...base };
  const primary500 = {
    h: base.h,
    s: clamp(Math.round(base.s * 0.9), 20, 90),
    l: clamp(base.l + 6, 18, 90),
  };
  const primary100 = {
    h: base.h,
    s: clamp(Math.round(base.s * 0.35), 8, 35),
    l: clamp(base.l + 35, 70, 96),
  };

  const root = document.documentElement.style;
  root.setProperty('--primary', toHslTriplet(primary600));
  root.setProperty('--ring', toHslTriplet(primary600));
  root.setProperty('--accent-strong', toHslTriplet(primary500));
  root.setProperty('--secondary', toHslTriplet(primary100));
  root.setProperty('--accent', toHslTriplet(primary100));
  root.setProperty('--color-primary-50', `hsl(${toHslTriplet(primary100)})`);
  root.setProperty('--focus-shadow', `0 0 0 3px hsl(${toHslTriplet(primary600)} / 0.18)`);
};
